/**
 * Edpies — Student Home Dashboard (Seneca-style answer to "Where am I?
 * What should I do next? How am I progressing?").
 *
 * Canonical /welcome surface for ALL student stages: 7+, 11+, KS3, GCSE.
 * Stage-aware copy comes from `progress.user.examTarget` / yearGroup.
 *
 * Pulls data from:
 *   GET /api/gamification/profile   — XP, Level, Streak
 *   GET /api/learning-path/today    — today's 3 unique subject tasks
 *   GET /api/learning-path/progress — weekly progress + current week
 *   GET /api/student/dashboard      — recent subject scores
 *
 * "Continue Learning" reads sessionStorage.edpies.activeLearningTask
 * (set by LearningTask.js on entry) or falls back to today's first task.
 *
 * Each Today's Learning Journey card carries a "Why this lesson?" tooltip
 * that surfaces the adaptive reasoning to the student (and to parents
 * during show-and-tell).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Sparkles, Brain, Target, Info,
  CalendarDays, ChevronRight, Clock, LogOut, ArrowRight,
  GraduationCap, AlertCircle, LayoutDashboard, Wand2, Lightbulb,
  Atom, Compass, ClipboardCheck,
} from 'lucide-react';
import SubjectThumbnail from '@/components/SubjectThumbnail';
import TileEmblem from '@/components/TileEmblem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const greetingForHour = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const StudentHome = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const userName = progress?.user?.name?.split(' ')[0] || 'Learner';

  // Stage-aware label for the sub-greeting line. Falls back gracefully when
  // examTarget isn't populated (older accounts).
  const examTarget = (
    progress?.user?.examTarget
    || progress?.user?.studentProfile?.examTarget
    || progress?.user?.studentProfile?.examGoal
    || ''
  );
  const yearGroup = String(
    progress?.user?.yearGroup
    || progress?.user?.studentProfile?.yearGroup
    || ''
  ).toLowerCase();
  const stageLabel = (() => {
    const t = examTarget.toLowerCase();
    if (t.includes('11') || /year[\s-]?[56]\b/.test(yearGroup)) return '11+ prep';
    if (t.includes('7') || /year[\s-]?[12]\b/.test(yearGroup)) return '7+ prep';
    if (t.includes('gcse') || /year[\s-]?1[01]\b/.test(yearGroup)) return 'GCSE prep';
    if (/year[\s-]?[789]\b/.test(yearGroup)) return 'KS3 prep';
    return 'Exam prep';
  })();

  const [gam, setGam] = useState(null);                 // gamification profile
  const [today, setToday] = useState(null);             // /learning-path/today
  const [lpProgress, setLpProgress] = useState(null);   // /learning-path/progress
  const [dashStats, setDashStats] = useState(null);     // /student/dashboard
  const [loading, setLoading] = useState(true);

  // ---- Load every dashboard data source in parallel -------------------------
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`${API}/gamification/profile`, { headers }).catch(() => null),
      axios.get(`${API}/learning-path/today`, { headers }).catch(() => null),
      axios.get(`${API}/learning-path/progress`, { headers }).catch(() => null),
      axios.get(`${API}/student/dashboard`, { headers }).catch(() => null),
    ]).then(([gamRes, todayRes, lpRes, dashRes]) => {
      setGam(gamRes?.data || null);
      setToday(todayRes?.data || null);
      setLpProgress(lpRes?.data || null);
      setDashStats(dashRes?.data || null);
      setLoading(false);
    });

    // Mark the user as having seen the welcome flow so the auto-redirect logic
    // doesn't bounce them back here unnecessarily.
    if (updateProgress && !progress?.hasSeenWelcome) {
      updateProgress({ hasSeenWelcome: true, welcomeSeenAt: new Date().toISOString() });
    }
  }, []);

  // ---- Derived values -------------------------------------------------------
  // Level / XP / Streak are surfaced via the global <XpLevelBadge> (top-right
  // tap-to-expand on mobile). Keep these here only for the inline level-progress
  // strip inside the Continue Learning hero.
  const levelNumber = gam?.level?.current?.level || 1;
  const levelProgress = gam?.level?.progress ?? 0;

  // "Continue Learning" — sessionStorage stash from LearningTask.js
  const resume = useMemo(() => {
    try {
      const raw = sessionStorage.getItem('edpies.activeLearningTask');
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj?.deep_link && !obj?.topic_slug) return null;
      return {
        label: obj.topic_label || obj.label || 'Resume task',
        subject: obj.subject_label || obj.subject || '',
        link: obj.deep_link || `/learn/${obj.task_type || 'practice'}/${obj.subject || 'maths'}/${obj.topic_slug}`,
      };
    } catch {
      return null;
    }
  }, []);

  // Fallback resume: first incomplete task from today.
  const fallbackResume = useMemo(() => {
    const first = today?.tasks?.[0];
    if (!first) return null;
    return {
      label: first.topic_label,
      subject: first.subject_label,
      link: first.deep_link,
    };
  }, [today]);
  const continueCard = resume || fallbackResume;

  // Weekly goal: count modules completed in the current week.
  const weeklyGoal = useMemo(() => {
    if (!lpProgress?.weeks) return { done: 0, total: 5 };
    const currentWeek = lpProgress.weeks.find(
      (w) => w.week_number === lpProgress.current_week
    );
    return {
      done: currentWeek?.modules_completed?.length || 0,
      total: 5,
    };
  }, [lpProgress]);

  // Recent subject scores — keep top 3 by tests taken so the card is concise.
  const recentSubjectScores = useMemo(() => {
    const arr = dashStats?.subjectProgress || [];
    return [...arr]
      .sort((a, b) => (b.tests || 0) - (a.tests || 0))
      .slice(0, 3);
  }, [dashStats]);

  // Upcoming exam countdown — read from studentProfile.examDate when present
  // (we accept dd/mm/yyyy or ISO). Falls back to a friendly "Set your exam
  // date" prompt so the card still has a clear next action.
  const exam = useMemo(() => {
    const raw = progress?.user?.studentProfile?.examDate
      || progress?.user?.examDate
      || progress?.user?.studentProfile?.targetExamDate;
    if (!raw) return null;
    let d;
    try {
      if (typeof raw === 'string' && raw.includes('/')) {
        const [day, month, year] = raw.split('/');
        d = new Date(`${year}-${month}-${day}`);
      } else {
        d = new Date(raw);
      }
      if (Number.isNaN(d.getTime())) return null;
    } catch { return null; }
    const ms = d.getTime() - Date.now();
    const days = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    const examName = (
      progress?.user?.studentProfile?.examName
      || progress?.user?.studentProfile?.targetSchool
      || `${stageLabel.replace(' prep', '')} Exam`
    );
    return { name: examName, days };
  }, [progress, stageLabel]);

  // ---- "Explore more tools" tile grid -------------------------------------
  // Stage-aware list of secondary tools — gives the dashboard a clear path
  // to every feature the legacy WelcomeScreen used to expose (Resources Hub,
  // adaptive assessment, AI Hub, STEM Library, custom practice papers, etc.)
  // so the new landing page never feels like a dead-end.
  const exploreTools = useMemo(() => {
    const yg = String(
      progress?.user?.yearGroup
      || progress?.user?.studentProfile?.yearGroup
      || ''
    ).toLowerCase();
    const target = String(
      progress?.user?.examTarget
      || progress?.user?.studentProfile?.examTarget
      || ''
    ).toLowerCase();
    const isGcseStage =
      /year[- ]?(9|10|11)/.test(yg) || yg.includes('gcse') || yg.includes('ks4') ||
      target === 'gcse' || target === 'ks4';
    const isElevenPlus =
      target.includes('11') || /year[- ]?[56]\b/.test(yg);

    const tools = [
      {
        id: 'adaptive',
        title: 'Take Adaptive Assessment',
        sub: 'Quick diagnostic to tune your 16-week plan',
        icon: Brain,
        accent: 'from-violet-500 to-indigo-600',
        pattern: 'rings',
        link: '/ks2-assessment',
        badge: 'Recommended',
      },
      {
        id: 'practice-hub',
        title: isElevenPlus ? 'Practice Papers & Mocks' : 'Practice Hub',
        sub: isElevenPlus
          ? 'Sutton SET · GL · CEM · ISEB · Skill Builder'
          : 'Exam-board practice & mocks',
        icon: GraduationCap,
        accent: 'from-purple-500 to-fuchsia-600',
        pattern: 'bars',
        link: '/practice-hub',
      },
      {
        id: 'resources',
        title: 'Resources Hub',
        sub: '5-min concepts · worked examples · 11+ twists',
        icon: Lightbulb,
        accent: 'from-rose-500 to-pink-600',
        pattern: 'waves',
        link: '/resources-hub',
      },
      {
        id: 'ai-hub',
        title: 'AI Learning Hub',
        sub: 'Reading Lab · Writing Coach · Tutor chat',
        icon: Sparkles,
        accent: 'from-amber-500 to-orange-500',
        pattern: 'sparkles',
        link: '/ai-hub',
      },
      {
        id: 'growth',
        title: 'My Growth Zone',
        sub: 'Custom AI practice papers by topic',
        icon: Wand2,
        accent: 'from-pink-500 to-purple-600',
        pattern: 'sparkles',
        link: '/ai-practice',
      },
    ];
    if (isGcseStage) {
      tools.push({
        id: 'stem',
        title: 'STEM Library',
        sub: 'Biology · Chemistry · Physics · Maths — Seneca-style',
        icon: Atom,
        accent: 'from-cyan-500 to-blue-600',
        pattern: 'rings',
        link: '/gcse-stem',
        badge: 'GCSE',
      });
    }
    if (isGcseStage || isElevenPlus) {
      tools.unshift({
        id: 'mark-my-work',
        title: 'Mark My Work',
        sub: 'Upload your work → instant AI marking, grade & examiner feedback',
        icon: ClipboardCheck,
        accent: 'from-emerald-500 to-teal-600',
        pattern: 'bars',
        link: '/mark-my-work',
        badge: 'Free for 5 days',
        highlight: true,
      });
    }
    tools.push({
      id: 'explore',
      title: 'Explore Everything',
      sub: 'English Library, Challenger Series, mocks & more',
      icon: Compass,
      accent: 'from-slate-700 to-slate-900',
      pattern: 'dots',
      link: '/explore',
    });
    return tools;
  }, [progress]);

  // ----- Render ----------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white dark:from-slate-950 dark:via-indigo-950 dark:to-slate-950 flex items-center justify-center">
        <div className="text-slate-500 dark:text-slate-300">Loading your day…</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white dark:from-slate-950 dark:via-indigo-950 dark:to-slate-950"
      data-testid="student-home"
    >
      {/* Top bar — Dashboard + Logout (XP badge is global, top-right) */}
      <div className="absolute top-3 left-3 z-30 flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/student-dashboard')}
          className="text-slate-600 hover:bg-slate-200/60 dark:text-slate-300 dark:hover:bg-slate-800/60"
          data-testid="dashboard-link"
        >
          <LayoutDashboard className="h-4 w-4 mr-1.5" />
          <span className="hidden sm:inline">Dashboard</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            localStorage.clear();
            if (updateProgress) updateProgress(null);
            window.location.replace('/');
          }}
          className="text-slate-600 hover:bg-slate-200/60 dark:text-slate-300 dark:hover:bg-slate-800/60"
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4 mr-1.5" />
          <span className="hidden sm:inline">Logout</span>
        </Button>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 sm:pt-16 pb-12 space-y-6">
        {/* --- Greeting + sub-line --- */}
        <motion.section
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-1.5 pr-16 sm:pr-24"
        >
          <h1
            className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white"
            data-testid="home-greeting"
          >
            {greetingForHour()}, {userName} <span className="inline-block">👋</span>
          </h1>
          <p
            className="text-sm text-slate-500 dark:text-slate-400"
            data-testid="home-stage-subline"
          >
            {today?.day || 'Today'} · Week {today?.current_week || 1} of 16 · {stageLabel}
          </p>
        </motion.section>

        {/* --- Continue Learning hero card --- */}
        {continueCard && (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
          >
            <Card
              className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 text-white cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => navigate(continueCard.link)}
              data-testid="continue-learning-card"
            >
              <CardContent className="p-6 sm:p-7">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-white/70 font-semibold">
                      Continue Learning
                    </p>
                    <h2 className="text-xl sm:text-2xl font-bold mt-1 truncate" data-testid="continue-title">
                      {continueCard.label}
                    </h2>
                    {continueCard.subject && (
                      <p className="text-sm text-white/80 mt-1">{continueCard.subject}</p>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    size="lg"
                    className="bg-white text-violet-700 hover:bg-white/90 shrink-0 font-semibold shadow-md"
                    data-testid="continue-btn"
                  >
                    Resume <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </div>
                {/* Level progress strip */}
                <div className="mt-5">
                  <div className="flex items-center justify-between text-xs text-white/80 mb-1.5">
                    <span>Level {levelNumber} progress</span>
                    <span>{levelProgress}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-white/20 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-300 rounded-full transition-all"
                      style={{ width: `${levelProgress}%` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.section>
        )}

        {/* --- Today's Learning Journey --- */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          data-testid="today-journey"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              Today&apos;s Learning Journey
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/learning-path')}
              className="text-slate-500 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
              data-testid="view-plan-btn"
            >
              View full plan <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <TooltipProvider delayDuration={150}>
            {(today?.tasks || []).map((task, idx) => {
              // Build the "Why this lesson?" reason: combine the adaptive
              // difficulty target with the student's last score in this
              // subject (if any) and the task kind. Falls back to a
              // friendly explanation for brand-new accounts.
              const subjectStat = (dashStats?.subjectProgress || []).find(
                (s) => (s.name || '').toLowerCase().includes(task.subject.split('_')[0])
              );
              const lastScore = subjectStat?.score != null ? Math.round(subjectStat.score) : null;
              const diffLabelMap = {
                foundation: 'Foundation',
                secure: 'Secure',
                greater_depth: 'Greater Depth',
                exam_ready: 'Exam-Ready',
                stretch: 'Stretch / Scholarship',
              };
              const diffLabel = diffLabelMap[task.target_difficulty] || 'Greater Depth';
              let reason;
              if (task.kind === 'mock') {
                reason = `Friday recap: a short mock so you finish the week practising the topics you covered. Pitched at ${diffLabel} level.`;
              } else if (lastScore != null && lastScore < 60) {
                reason = `You scored ${lastScore}% in ${task.subject_label} recently — today's lesson rebuilds the foundation at ${diffLabel} level.`;
              } else if (lastScore != null && lastScore >= 75) {
                reason = `You're strong in ${task.subject_label} (${lastScore}%) — today's lesson stretches you with ${diffLabel} content.`;
              } else if (lastScore != null) {
                reason = `${task.subject_label} sits around ${lastScore}% — today's lesson reinforces the topic at ${diffLabel} level.`;
              } else {
                reason = `Picked from your 16-week ${task.subject_label} ladder. Pitched at ${diffLabel} so the difficulty matches your last diagnostic.`;
              }

              return (
                <Card
                  key={task.task_id}
                  className="group cursor-pointer hover:shadow-md transition-shadow border border-slate-200 dark:border-slate-700 dark:bg-slate-900/60"
                  onClick={() => navigate(task.deep_link)}
                  data-testid={`today-task-${idx}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <SubjectThumbnail subject={task.subject} size="md" />
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {task.duration_minutes} mins
                        </span>
                        {/* "Why this lesson?" reveal — stop propagation so the
                            card's own onClick (navigate) doesn't also fire. */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center justify-center h-6 w-6 rounded-full text-slate-400 hover:text-violet-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition focus:outline-none focus:ring-2 focus:ring-violet-500"
                              aria-label="Why this lesson?"
                              data-testid={`today-task-${idx}-why`}
                            >
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            align="end"
                            className="max-w-xs text-xs leading-relaxed"
                            data-testid={`today-task-${idx}-why-tooltip`}
                          >
                            <p className="font-semibold mb-1">Why this lesson?</p>
                            <p>{reason}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </div>
                    <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
                      {task.subject_label}
                    </p>
                    <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5 line-clamp-2">
                      {idx + 1}. {task.topic_label}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-3 px-0 text-violet-700 dark:text-violet-300 hover:bg-transparent"
                      data-testid={`today-task-cta-${idx}`}
                    >
                      {task.cta} <ChevronRight className="h-4 w-4 ml-0.5 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
            </TooltipProvider>
          </div>
        </motion.section>

        {/* --- Two-column: Upcoming Exam + Weekly Goal --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Upcoming Exam */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
          >
            <Card
              className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60"
              data-testid="upcoming-exam-card"
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 text-white flex items-center justify-center shadow-sm shrink-0">
                    <CalendarDays className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
                      Upcoming Exam
                    </p>
                    {exam ? (
                      <>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">
                          {exam.name}
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                          <span className="font-semibold text-rose-600 dark:text-rose-400">
                            {exam.days} {exam.days === 1 ? 'day' : 'days'} remaining
                          </span>
                        </p>
                      </>
                    ) : (
                      <>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">
                          Set your exam date
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                          Add your target exam to unlock a personalised countdown.
                        </p>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => navigate('/profile')}
                          className="px-0 mt-1 text-rose-600 dark:text-rose-400"
                          data-testid="set-exam-date-btn"
                        >
                          Update profile <ChevronRight className="h-4 w-4 ml-0.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Weekly Goal */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Card
              className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60"
              data-testid="weekly-goal-card"
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-sm shrink-0">
                    <Target className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
                      Weekly Goal
                    </p>
                    <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">
                      {weeklyGoal.done}/{weeklyGoal.total} tasks complete
                    </h3>
                    <Progress
                      value={(weeklyGoal.done / weeklyGoal.total) * 100}
                      className="mt-3 h-2"
                      data-testid="weekly-goal-progress"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                      Hit 5/5 to unlock this week&apos;s mock paper.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* --- Recent Scores --- */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              Recent Scores
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/student-dashboard')}
              className="text-slate-500 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
              data-testid="view-progress-btn"
            >
              See progress <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          <Card className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60" data-testid="recent-scores-card">
            <CardContent className="p-5">
              {recentSubjectScores.length === 0 ? (
                <div className="text-center py-6">
                  <AlertCircle className="h-8 w-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Take a mock or practice paper to see your scores here.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentSubjectScores.map((s, i) => {
                    const pct = Math.round(s.score || 0);
                    const subj = (s.name || '').toLowerCase();
                    const accent =
                      pct >= 75 ? 'bg-emerald-500'
                        : pct >= 60 ? 'bg-amber-500'
                          : 'bg-rose-500';
                    return (
                      <div key={i} className="flex items-center gap-4" data-testid={`recent-score-${i}`}>
                        <p className="w-24 text-sm font-semibold text-slate-700 dark:text-slate-200 capitalize">
                          {s.name}
                        </p>
                        <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${accent} transition-all`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="w-12 text-sm font-bold text-slate-900 dark:text-white text-right">
                          {pct}%
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.section>

        {/* --- Explore more tools (stage-aware tile grid) --- */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          data-testid="explore-section"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              Explore more tools
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/explore')}
              className="text-slate-500 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
              data-testid="explore-all-btn"
            >
              See all <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {exploreTools.map((tool) => (
                <Card
                  key={tool.id}
                  onClick={() => navigate(tool.link)}
                  className={`cursor-pointer transition-all border dark:bg-slate-900/60 ${
                    tool.highlight
                      ? 'sm:col-span-2 lg:col-span-3 ring-2 ring-emerald-400 border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/40 hover:shadow-lg hover:-translate-y-0.5'
                      : 'border-slate-200 dark:border-slate-700 hover:shadow-md'
                  }`}
                  data-testid={`explore-tool-${tool.id}`}
                >
                  <CardContent className="p-4 flex items-start gap-3">
                    <TileEmblem
                      icon={tool.icon}
                      accent={tool.accent}
                      pattern={tool.pattern}
                      size={tool.highlight ? 'lg' : 'md'}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-bold text-slate-900 dark:text-white ${tool.highlight ? 'text-lg' : 'text-base'}`}>{tool.title}</h3>
                        {tool.badge && (
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            tool.highlight
                              ? 'bg-emerald-600 text-white'
                              : 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
                          }`}>
                            {tool.badge}
                          </span>
                        )}
                        {tool.highlight && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white text-emerald-700 border border-emerald-300">New</span>
                        )}
                      </div>
                      <p className={`text-slate-500 dark:text-slate-400 mt-0.5 ${tool.highlight ? 'text-sm' : 'text-xs line-clamp-2'}`}>{tool.sub}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-400 dark:text-slate-500 shrink-0 mt-1" />
                  </CardContent>
                </Card>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default StudentHome;
